import axios from 'axios'
import { isLocalSamlBypassEnabled } from './authBypass'

// Injected at app boot to avoid circular deps: store → adminSlices → adminActions → request → store
let onAuthError = null
let getToken = null

export function setAuthErrorHandler(fn) {
  onAuthError = fn
}

/** Call from app init with () => store.getState().login.token so token stays in Redux only, not localStorage */
export function setTokenGetter(fn) {
  getToken = fn
}

// API base for all requests (including /auth/logout). Use env vars so dev can hit local backend.
const apiBase =
  (process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_PRODUCTION_BASE_URL
    : process.env.REACT_APP_DEVELOPMENT_BASE_URL) || 'https://api.bellhopt.com/api/v1';
const request = axios.create({
  baseURL: String(apiBase).trim(),
  withCredentials: true,
})

// Cookie is primary auth; add Bearer only when token exists in Redux (never read from localStorage)
request.interceptors.request.use(
    (config) => {
        const token = getToken ? getToken() : null
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => Promise.reject(error)
)

request.interceptors.response.use(
    response => response,
    error => {
        if (!error.response) {
            const error = {
                error: 'No response from the server, please try again later',
            };
            const data = { response: { data: error } };
            return Promise.reject(data);
        }

        // Industry standard: Auto-logout on auth errors
        // If any API call returns 401/403/440, session is invalid - clear it
        const status = error.response?.status
        if (status === 401 || status === 403 || status === 440) {
            // Local dev bypass mode: avoid forced redirect/logout loops when
            // backend auth/session is intentionally bypassed in UI.
            if (isLocalSamlBypassEnabled()) {
                return Promise.reject(error)
            }
            // Don't logout if we're already on login page (avoid loops)
            if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
                if (process.env.NODE_ENV !== 'production') {
                    console.warn('[request] Session expired - auto-logout');
                }
                if (onAuthError) onAuthError()
            }
        }

        return Promise.reject(error);
    },
);

export default request